import React, { useRef, useCallback } from 'react';
import { Loader2, Camera } from 'lucide-react';
import { useProfilePhoto } from '@/features/profile/hooks/useProfilePhoto';
import { useToast } from '@/components/ui/use-toast';
import { ProfilePhotoUploadProps } from './ProfilePhotoUpload.types';

const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  currentPhotoUrl,
  onPhotoUpdate,
  onError,
  className = '',
  size = 'lg'
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { state, uploadPhoto, clearError } = useProfilePhoto();

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    clearError();

    const result = await uploadPhoto(file);

    if (result.success && result.url) {
      onPhotoUpdate(result.url);
      toast({
        title: 'Photo uploaded successfully!',
        description: 'Your profile photo has been updated.',
        variant: 'default'
      });
    } else {
      const errorMessage = result.error || 'Failed to upload photo';
      onError(errorMessage);
      toast({
        title: 'Upload failed',
        description: errorMessage,
        variant: 'destructive'
      });
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadPhoto, onPhotoUpdate, onError, clearError, toast]);

  const handleUploadClick = useCallback(() => {
    if (state.isUploading || state.isDeleting) return;
    fileInputRef.current?.click();
  }, [state.isUploading, state.isDeleting]);

  const isLoading = state.isUploading || state.isDeleting;

  return (
    <div className={className}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        onChange={handleFileSelect}
        className="hidden"
        disabled={isLoading}
      />

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center z-40">
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
      )}

      {/* Camera button - bottom left */}
      {!isLoading && (
        <button
          type="button"
          onClick={handleUploadClick}
          className="absolute bottom-0 left-0 w-7 h-7 bg-sossiety-red text-white rounded-full flex items-center justify-center hover:bg-sossiety-red-hover transition-colors z-50 shadow-lg border-2 border-white"
          title="Upload photo"
        >
          <Camera className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

export default ProfilePhotoUpload;

